/**
 * @module byo-bundle
 * Entry point — defines the <byo-bundle> custom element: one slot row and
 * drawer per bundle slot, and one ADD TO CART for every slot's variant.
 * Display only; the Discount Function prices the cart.
 */

import { roll } from './celebrate.js';
import { CLASSES, SELECTORS } from './constants.js';
import { closeDrawer, openDrawer, releaseScroll } from './drawer.js';
import { renderSlot, renderSummary } from './render.js';
import {
  createSlots,
  filledCount,
  isComplete,
  removeVariant,
  selectVariant,
  type Slots,
} from './state.js';
import { addWithFeedback } from '../virtual-collection/cart.js';
import type { CartElement } from '../virtual-collection/cart.js';
import { getQuantity } from '../virtual-collection/ui.js';

export class ByoBundle extends HTMLElement {
  declare slots: Slots;
  declare cart: CartElement | null;
  declare _onClick: EventListener;

  constructor() {
    super();
    this._onClick = (evt) => void this.onClick(evt);
  }

  connectedCallback() {
    // Kept across a move or re-attach: the rendered rows and CTA still reflect it.
    this.slots ??= createSlots(this.querySelectorAll(SELECTORS.slot).length);
    this.cart =
      (document.querySelector('cart-notification') as (Element & CartElement) | null) ||
      (document.querySelector('cart-drawer') as (Element & CartElement) | null);

    if (document.querySelector('cart-drawer')) {
      this.querySelector(SELECTORS.submitButton)?.setAttribute('aria-haspopup', 'dialog');
    }

    this.querySelectorAll(SELECTORS.drawer).forEach((dialog) =>
      dialog.addEventListener('close', releaseScroll),
    );
    this.addEventListener('click', this._onClick);
    // Kept slots survive a move or re-attach, so mark the first empty one, not the first.
    this.querySelectorAll(SELECTORS.slot)[this.slots.indexOf(null)]?.classList.add(CLASSES.next);
  }

  disconnectedCallback() {
    // Removing an open modal <dialog> fires no `close`, so release the lock here.
    if (this.querySelector(`${SELECTORS.drawer}[open]`)) releaseScroll();
    this.removeEventListener('click', this._onClick);
    this.querySelectorAll(SELECTORS.drawer).forEach((dialog) =>
      dialog.removeEventListener('close', releaseScroll),
    );
  }

  async onClick(evt: Event) {
    const target = evt.target as HTMLElement;
    const slot = target.closest<HTMLElement>(SELECTORS.slot);
    const index = slot ? Number(slot.dataset.slotIndex) : -1;
    const dialog = slot?.querySelector<HTMLDialogElement>(SELECTORS.drawer);

    if (target === dialog) {
      // Click on the ::backdrop lands on the dialog itself.
      closeDrawer(dialog);
    } else if (dialog && target.closest(SELECTORS.open)) {
      openDrawer(dialog);
    } else if (dialog && target.closest(SELECTORS.close)) {
      closeDrawer(dialog);
    } else if (slot && dialog && target.closest(SELECTORS.select)) {
      const variantId = Number(target.closest<HTMLElement>(SELECTORS.card)!.dataset.variantId);
      if (this.slots[index] === variantId) {
        this.update(slot, index, removeVariant(this.slots, index));
      } else {
        this.update(slot, index, selectVariant(this.slots, index, variantId));
        closeDrawer(dialog);
      }
    } else if (slot && target.closest(SELECTORS.remove)) {
      this.update(slot, index, removeVariant(this.slots, index));
      // Remove hides with the emptied row; keep focus on the row instead of <body>.
      slot.querySelector<HTMLElement>(SELECTORS.open)!.focus();
    } else if (slot && target.closest(SELECTORS.addSingle)) {
      await this.addSingle(target.closest<HTMLButtonElement>(SELECTORS.addSingle)!, slot, index);
    } else if (target.closest(SELECTORS.submitButton)) {
      await this.submit();
    }
  }

  update(slot: HTMLElement, index: number, slots: Slots) {
    const before = this.slots;
    this.slots = slots;
    this.clearErrors();
    renderSlot(slot, slots[index]);
    renderSummary(this, slots);
    roll(this.querySelector(SELECTORS.count), filledCount(before), filledCount(slots));
  }

  clearErrors() {
    this.querySelectorAll<HTMLElement>(SELECTORS.errorWrapper).forEach((w) => (w.hidden = true));
  }

  async addSingle(button: HTMLButtonElement, slot: HTMLElement, index: number) {
    const variantId = this.slots[index];
    if (variantId) await this.addToCart(button, slot, [variantId], 1);
  }

  async submit() {
    const button = this.querySelector<HTMLButtonElement>(SELECTORS.submitButton)!;
    if (!isComplete(this.slots)) {
      const empty = this.querySelectorAll(SELECTORS.slot)[this.slots.indexOf(null)];
      empty?.querySelector<HTMLElement>(SELECTORS.open)?.focus();
      return;
    }
    const cta = button.closest<HTMLElement>(SELECTORS.cta)!;
    await this.addToCart(
      button,
      cta,
      this.slots as number[],
      getQuantity(this.querySelector<HTMLInputElement>(SELECTORS.stepperValue)),
    );
    // clearLoading drops aria-disabled even if a slot was emptied during the add.
    renderSummary(this, this.slots);
  }

  addToCart(button: HTMLElement, errorScope: HTMLElement, ids: number[], quantity: number) {
    return addWithFeedback({
      button,
      root: this,
      errorScope,
      cart: this.cart,
      variants: ids.map((id) => ({ id, available: true })),
      quantity,
    });
  }
}

if (!customElements.get('byo-bundle')) {
  customElements.define('byo-bundle', ByoBundle);
}
